import { useState, useEffect, useCallback } from 'react';

type BrushSelection = {
  startIndex: number;
  endIndex: number;
  startTimestamp: string | null;
  endTimestamp: string | null;
  isStartExtremity: boolean;
  isEndExtremity: boolean;
};

type DataPoint = {
  time: string;
  [key: string]: unknown;
};

type UseBrushSelectionResult = {
  brushState: BrushSelection;
  handleBrushChange: (params: { startIndex?: number; endIndex?: number }) => void;
};

export const useBrushSelection = <T extends DataPoint>(
  data: T[] | undefined
): UseBrushSelectionResult => {
  const [brushState, setBrushState] = useState<BrushSelection>({
    startIndex: 0,
    endIndex: data?.length ? data.length - 1 : 0,
    startTimestamp: data?.[0]?.time ?? null,
    endTimestamp: data?.[data.length - 1]?.time ?? null,
    isStartExtremity: true,
    isEndExtremity: true,
  });

  useEffect(() => {
    if (!data || data.length === 0) return;

    const findClosestIndex = (timestamp: string | null): number => {
      if (!timestamp) return 0;
      const idx = data.findIndex((d) => d.time === timestamp);
      if (idx !== -1) return idx;

      const targetTime = new Date(timestamp).getTime();
      let closestIdx = 0;
      let minDiff = Math.abs(new Date(data[0].time).getTime() - targetTime);

      for (let i = 1; i < data.length; i++) {
        const diff = Math.abs(new Date(data[i].time).getTime() - targetTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }

      return closestIdx;
    };

    setBrushState((prev) => {
      const newStartIndex = prev.isStartExtremity ? 0 : findClosestIndex(prev.startTimestamp);
      const newEndIndex = prev.isEndExtremity
        ? data.length - 1
        : findClosestIndex(prev.endTimestamp);

      const actualStartIndex = Math.max(0, Math.min(newStartIndex, data.length - 1));
      const actualEndIndex = Math.max(0, Math.min(newEndIndex, data.length - 1));

      return {
        startIndex: actualStartIndex,
        endIndex: actualEndIndex,
        startTimestamp: prev.isStartExtremity ? data[0].time : prev.startTimestamp,
        endTimestamp: prev.isEndExtremity ? data[data.length - 1].time : prev.endTimestamp,
        isStartExtremity: prev.isStartExtremity,
        isEndExtremity: prev.isEndExtremity,
      };
    });
  }, [data]);

  const handleBrushChange = useCallback(
    ({ startIndex, endIndex }: { startIndex?: number; endIndex?: number }) => {
      if (startIndex === undefined || endIndex === undefined || !data) return;

      const isStartAtExtremity = startIndex === 0;
      const isEndAtExtremity = endIndex === data.length - 1;

      setBrushState({
        startIndex,
        endIndex,
        startTimestamp: data[startIndex]?.time ?? null,
        endTimestamp: data[endIndex]?.time ?? null,
        isStartExtremity: isStartAtExtremity,
        isEndExtremity: isEndAtExtremity,
      });
    },
    [data]
  );

  return {
    brushState,
    handleBrushChange,
  };
};
